import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Card, Button, Input, Avatar, Badge } from '../components/ui';
import { 
  User, 
  Mail, 
  Crown,
  LogOut,
  Trash2,
  Save,
  Loader2
} from 'lucide-react';

const Settings = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: formData.name }),
      });
      if (!response.ok) {
        throw new Error('Profile update failed');
      }
    } catch (err) {
      // Profile update failed silently
    } finally {
      setLoading(false);
    }
  };

  const planDetails = {
    free: {
      name: 'Ücretsiz',
      color: 'secondary',
      features: [t('settings.feat3tattoos', '3 tattoo records'), t('settings.featBasicReminders', 'Basic reminders')]
    },
    monthly: {
      name: t('settings.premiumMonthly', 'Premium Monthly'),
      color: 'default',
      features: [t('settings.featUnlimited', 'Unlimited tattoos'), t('settings.featAI', 'AI analysis'), t('settings.featAll', 'All features')]
    },
    yearly: {
      name: t('settings.premiumYearly', 'Premium Yearly'),
      color: 'default',
      features: [t('settings.featUnlimited', 'Unlimited tattoos'), t('settings.featAI', 'AI analysis'), t('settings.featAR', 'AR preview'), t('settings.featAll', 'All features')]
    }
  };

  const currentPlan = planDetails[user?.subscription_plan] || planDetails.free;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl mb-1" data-testid="settings-title">
            {t('settings.account', 'Account')} <span className="text-primary">{t('settings.settings', 'Settings')}</span>
          </h1>
          <p className="text-muted-foreground">
            {t('settings.subtitle', 'Manage your profile and subscription')}
          </p>
        </div>

        <div className="space-y-6">
          {/* Profile Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-6">
              <h2 className="font-heading text-xl mb-6">{t('settings.profileInfo', 'Profile Information')}</h2>
              
              <div className="flex items-start gap-6 mb-6">
                <Avatar
                  src={user?.picture}
                  alt={user?.name}
                  fallback={user?.name?.charAt(0)}
                  size="xl"
                />
                <div className="flex-1">
                  <h3 className="font-heading text-lg">{user?.name}</h3>
                  <p className="text-muted-foreground text-sm">{user?.email}</p>
                  <Badge variant={currentPlan.color} className="mt-2">
                    <Crown className="w-3 h-3 mr-1" />
                    {currentPlan.name}
                  </Badge>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    {t('auth.name', 'Full Name')}
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder={t('settings.namePlaceholder', 'Your name')}
                    data-testid="settings-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    {t('auth.email', 'Email')}
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="E-posta adresiniz"
                    disabled
                    data-testid="settings-email-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('settings.emailCantChange', 'Email cannot be changed')}
                  </p>
                </div>

                <Button onClick={handleSave} disabled={loading} data-testid="save-settings-btn">
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {t('common.save', 'Save')}
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Subscription Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-6">
              <h2 className="font-heading text-xl mb-6">{t('settings.subscription', 'Subscription')}</h2>
              
              <div className="p-4 bg-muted rounded-sm mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-heading text-lg">{currentPlan.name}</h3>
                    <p className="text-sm text-muted-foreground">{t('settings.currentPlan', 'Your current plan')}</p>
                  </div>
                  <Crown className="w-8 h-8 text-primary" />
                </div>
                <ul className="space-y-1">
                  {currentPlan.features.map((feature, i) => (
                    <li key={i} className="text-sm text-muted-foreground">• {feature}</li>
                  ))}
                </ul>
              </div>

              {user?.subscription_plan === 'free' && (
                <Link to="/pricing">
                  <Button className="w-full" data-testid="upgrade-btn">
                    <Crown className="w-4 h-4 mr-2" />
                    {t('settings.upgradePremium', 'Upgrade to Premium')}
                  </Button>
                </Link>
              )}
            </Card>
          </motion.div>

          {/* Danger Zone */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-6 border-destructive/30">
              <h2 className="font-heading text-xl mb-4 text-destructive">{t('settings.dangerZone', 'Danger Zone')}</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-muted rounded-sm">
                  <div>
                    <h3 className="font-medium">{t('nav.logout', 'Logout')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.logoutDesc', 'Securely sign out of your account')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleLogout}
                    data-testid="settings-logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout', 'Logout')}
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 bg-destructive/10 rounded-sm">
                  <div>
                    <h3 className="font-medium text-destructive">{t('settings.deleteAccount', 'Delete Account')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.deleteDesc', 'Permanently delete your account and all data')}
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    data-testid="delete-account-btn"
                    onClick={async () => {
                      if (window.confirm(t('settings.deleteConfirm', 'Are you sure? This action cannot be undone. All your data will be permanently deleted.'))) {
                        try {
                          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
                            method: 'DELETE',
                            credentials: 'include',
                          });
                          await logout();
                          navigate('/');
                        } catch (err) {
                          // Delete failed
                        }
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('settings.deleteAccount', 'Delete Account')}
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
